import { CustomSearchField } from '@biq/apex-ui';
import { Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { HelixLoader } from '../../../components';
import { useSearchMetadata } from '../../../hooks';
import { METADATA_STORAGE_KEY } from '../metadata-search-helpert';
import {
    emptyDataResults,
    metadataSections,
    MetadataResultAccordion,
    metadataResultStyles,
    MetadataSearchResultsProps,
    MetadataSectionKey,
    METADATA_SEARCH_TERMS_LIMIT,
} from './metadata-components.helper';
import { ResultsAccordion } from './ResultsAccordion';

export const MetadataSearchResults = ({
    initialTerms,
}: MetadataSearchResultsProps): React.JSX.Element => {
    const [resultString, setResultString] = useState<string>('');
    const [refetchOnLoad, setRefetchOnLoad] = useState<boolean>(true);
    const [areAccordionsReady, setAreAccordionsReady] =
        useState<boolean>(false);
    const [searchTerms, setSearchTerms] = useState<string[]>([]);
    const [expanded, setExpanded] = useState<string | boolean>('');
    const [accordions, setAccordions] = useState<MetadataResultAccordion[]>([]);

    const {
        data,
        isFetched,
        isLoading,
        isError,
        isSuccess,
        refetch: searchMetadataTerms,
    } = useSearchMetadata({
        terms: searchTerms,
    });

    const handleChange = (isExpanded: boolean, panel: string): void => {
        setExpanded(isExpanded ? panel : false);
    };

    const setAccordionsData = (searchTermData: any): void => {
        const newAccordions: MetadataResultAccordion[] = [];
        Object.keys(searchTermData).forEach((key, index) => {
            const convertedKey: MetadataSectionKey = key as MetadataSectionKey;

            newAccordions.push({
                name: `panel${index}`,
                accordionAriaControls: `panel${index}-content`,
                accordionSumID: `panel${index}-header`,
                accordionHeader: metadataSections[convertedKey] ?? '',
                key: convertedKey,
                data: searchTermData[key] ?? [],
            });
        });

        // sort data by highest data length first, and then by accordion header in alphabetical order.
        newAccordions.sort(
            (a: MetadataResultAccordion, b: MetadataResultAccordion) => {
                return (
                    b.data.length - a.data.length ||
                    a.accordionHeader.localeCompare(b.accordionHeader)
                );
            }
        );

        setAreAccordionsReady(true);
        setAccordions(newAccordions);
    };

    const generateResultString = (terms: string[]): void => {
        if (terms.length) {
            setResultString(searchTerms.join(', '));
        }
    };

    useEffect(() => {
        if (initialTerms.length) {
            setSearchTerms(initialTerms);
            generateResultString(initialTerms);
        }
    }, [initialTerms]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (refetchOnLoad && searchTerms.length > 0) {
            // we should only trigger refetching the data on initial load or when user clicks on the submit button.
            searchMetadataTerms();
            setRefetchOnLoad(false);
            generateResultString(searchTerms);
            setAreAccordionsReady(false);
        }
    }, [searchTerms, refetchOnLoad]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isFetched && isSuccess && data) {
            // create accordions and save to local storage on successful data fetch.
            setAccordionsData(data?.data);
            localStorage.setItem(
                METADATA_STORAGE_KEY,
                JSON.stringify(searchTerms)
            );
        } else if (isError) {
            setAccordionsData(emptyDataResults);
        }
    }, [data, isFetched, isLoading, isError, isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = (): void => {
        setRefetchOnLoad(true);
    };

    return (
        <Grid sx={metadataResultStyles.resultsPageContainer}>
            <Grid>
                <CustomSearchField
                    customStyles={metadataResultStyles.customSearchFieldStyles}
                    fieldValue={searchTerms}
                    setFieldValue={setSearchTerms}
                    onButtonClick={onSubmit}
                    disabled={isLoading}
                    isCaseSensitive={false}
                    tagLimit={METADATA_SEARCH_TERMS_LIMIT}
                />
            </Grid>
            {isLoading && !areAccordionsReady ? (
                <Grid height={'100%'}>
                    <HelixLoader />
                </Grid>
            ) : (
                <>
                    <Grid sx={metadataResultStyles.resultTextContainer}>
                        <Typography sx={metadataResultStyles.resultText}>
                            Results for&nbsp;
                        </Typography>
                        <Typography sx={metadataResultStyles.boldedResultText}>
                            {resultString}
                        </Typography>
                    </Grid>
                    <Grid>
                        {accordions.map((accordion) => (
                            <ResultsAccordion
                                key={accordion.key}
                                data={accordion.data}
                                accordionAriaControls={
                                    accordion.accordionAriaControls
                                }
                                accordionSumID={accordion.accordionSumID}
                                accordionHeader={accordion.accordionHeader}
                                expanded={expanded === accordion.name}
                                onChange={(_, isExpanded): void =>
                                    handleChange(isExpanded, accordion.name)
                                }
                            />
                        ))}
                    </Grid>
                </>
            )}
        </Grid>
    );
};
